import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["day", "month", "year", "fullDate", "error"];

  static values = {
    errorMessage: { type: String, default: "" },
  };

  connect() {
    this.min = 1;
    this.maxMonth = 12;

    // Validate on load if values are present
    if (
      this.dayTarget.value ||
      this.monthTarget.value ||
      this.yearTarget.value
    ) {
      this.validateFullDate();
    }
  }

  validateDay() {
    this.validateFullDate();
  }

  validateMonth() {
    this.validateFullDate();
  }

  validateYear() {
    this.validateFullDate();
  }

  validateFullDate() {
    const day = parseInt(this.dayTarget.value, 10);
    const month = parseInt(this.monthTarget.value, 10);
    const year = parseInt(this.yearTarget.value, 10);

    // Clear if all fields are empty
    if (
      !this.dayTarget.value &&
      !this.monthTarget.value &&
      !this.yearTarget.value
    ) {
      this.clearError();
      if (this.hasFullDateTarget) {
        this.fullDateTarget.value = "";
      }
      return true;
    }

    // Check if all fields are filled
    if (
      !this.dayTarget.value ||
      !this.monthTarget.value ||
      !this.yearTarget.value
    ) {
      this.showError();
      if (this.hasFullDateTarget) {
        this.fullDateTarget.value = "";
      }
      return false;
    }

    // Validate year has 4 digits
    if (this.yearTarget.value.length !== 4) {
      this.showError();
      if (this.hasFullDateTarget) {
        this.fullDateTarget.value = "";
      }
      return false;
    }

    // Validate ranges
    if (!this.inRange(day, 1, 31) || !this.inRange(month, 1, 12)) {
      this.showError();
      if (this.hasFullDateTarget) {
        this.fullDateTarget.value = "";
      }
      return false;
    }

    // Validate calendar date (check for valid day/month combinations)
    if (!this.isValidCalendarDate(day, month, year)) {
      this.showError();
      if (this.hasFullDateTarget) {
        this.fullDateTarget.value = "";
      }
      return false;
    }

    // All valid - set the hidden field
    if (this.hasFullDateTarget) {
      // Format as YYYY-MM-DD without timezone conversion
      const yyyy = year.toString();
      const mm = month.toString().padStart(2, "0");
      const dd = day.toString().padStart(2, "0");
      this.fullDateTarget.value = `${yyyy}-${mm}-${dd}`;
    }

    this.clearError();
    return true;
  }

  // eslint-disable-next-line class-methods-use-this
  inRange(value, min, max) {
    return value >= min && value <= max;
  }

  // eslint-disable-next-line class-methods-use-this
  isValidCalendarDate(day, month, year) {
    // Create a date and check if it's valid
    const date = new Date(year, month - 1, day);

    // Check if the date components match the input
    // This catches invalid dates like Feb 30, Apr 31, etc.
    return (
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day
    );
  }

  showError() {
    // Add error styling to all three inputs
    [this.dayTarget, this.monthTarget, this.yearTarget].forEach((target) => {
      target.classList.add(
        "border-red-500",
        "focus:border-red-500",
        "focus:ring-red-500"
      );
      target.classList.remove("border-gray-300");
    });

    if (this.hasErrorTarget && this.errorMessageValue) {
      this.errorTarget.textContent = this.errorMessageValue;
      this.errorTarget.classList.remove("hidden");
    }
  }

  clearError() {
    // Remove error styling from all three inputs
    [this.dayTarget, this.monthTarget, this.yearTarget].forEach((target) => {
      target.classList.remove(
        "border-red-500",
        "focus:border-red-500",
        "focus:ring-red-500"
      );
      target.classList.add("border-gray-300");
    });

    if (this.hasErrorTarget) {
      this.errorTarget.textContent = "";
      this.errorTarget.classList.add("hidden");
    }
  }
}
